import { DateTime } from 'luxon';

/**
 * Verifica si la fecha ingresada ya ha pasado, ignorando el tiempo.
 *
 * @param {string} date Fecha en ISO 8601 ejemplo '2021-12-31'.
 * @param {Object} options Parámetros opcionales.
 * @param {string} options.message Un mensaje de error opcional para retornar si la fecha ya ha pasado.
 * @returns {boolean} Verdadero si la fecha ya ha pasado, falso si no.
 */
const dayHasPassed = (date, { message }) => {
  const today = DateTime.now();
  const compareDate = DateTime.fromISO(date);

  // Verificamos si el año, mes y día de la fecha a comparar son menores al año, mes y día actuales.
  const toCheck = [
    today.startOf('year') > compareDate.startOf('year'),
    today.startOf('month') > compareDate.startOf('month'),
    today.startOf('day') > compareDate.startOf('day'),
  ];

  // Si alguna de las condiciones anteriores se cumple, entonces la fecha ya ha pasado.
  const hasPassed = toCheck.some(check => check);

  // Si se proporciona un mensaje de error, se retorna el mensaje si la fecha ya ha pasado.
  if (hasPassed && message) {
    return message;
  }

  // Si no se cumple ninguna de las condiciones anteriores, entonces la fecha no ha pasado.
  return hasPassed;
};

/**
 * Verifica si una fecha dada ya ha pasado en comparación con la fecha actual.
 *
 * @param {string} date - La fecha con tiempo en formato ISO 8601, ejemplo '2021-12-31T23:59'.
 * @param {Object} [options] - Parámetros opcionales.
 * @param {string} options.message - Un mensaje de error opcional para retornar si la fecha ya ha pasado.
 * @returns {boolean|string} - Retorna verdadero si la fecha ya ha pasado y no se proporciona un mensaje,
 * de lo contrario, retorna el mensaje proporcionado si la fecha ya ha pasado.
 */
const dateHasPassed = (date, { message } = {}) => {
  const today = DateTime.now();
  const compareDate = DateTime.fromISO(date);

  // Si el año de la fecha a comparar es menor al año actual, entonces la fecha ya pasó.
  const hasPassed = compareDate.toMillis() < today.toMillis();

  // Si se proporciona un mensaje de error, se retorna el mensaje si la fecha ya pasó.
  if (hasPassed && message) {
    return message;
  }

  // Si no se proporciona un mensaje de error, se retorna verdadero si la fecha ya pasó.
  return hasPassed;
};

/**
 * Verifica si una fecha con tiempo ya ha pasado en comparación con la fecha y hora actuales.
 *
 * @param {string} datetime Fecha en formato ISO 8601 con hora y minutos, ejemplo '2021-12-31T23:59'.
 * @param {Object} options Parámetros opcionales.
 * @param {string} options.message Un mensaje de error opcional para retornar si la fecha ya ha pasado.
 * @return {boolean|string} Retorna booleano si la fecha ya ha pasado y no se proporciona un mensaje,
 * de lo contrario, retorna el mensaje proporcionado si la fecha ya ha pasado.
 */
const datetimeHasPassed = (datetime, { message }) => {
  if (!datetime) return message || false;

  const today = DateTime.now();
  const compareDate = DateTime.fromISO(datetime);

  // Verificamos si la fecha y hora a comparar es menor a la fecha y hora actuales.
  const hasPassed = compareDate.toMillis() < today.toMillis();

  // Si se propociona mensaje y ya paso la fecha mostramos el mensaje.
  if (hasPassed && message) return message;

  return hasPassed;
};

/**
 *  Valida la descripción de un curso en base a las siguientes condiciones:
 *
 * > La descripción no debe exceder de los 255 caracteres.
 *
 * @param {string} description La descripción del curso que se validara
 */
const courseDescription = (description = '') => {
  if (description.length > 255) {
    return 'La descripción no puede exceder los 255 caracteres.';
  }
  return '';
};

/**
 * Método para validar las propiedades de una contraseña, se evalúabn las siguientes condiciones:
 * - La contraseña debe contener al menos 6 caracteres.
 * - La contraseña no debe contener espacios en blanco.
 * - La contraseña no puede estar vacía.
 * @param {string} password La contraseña a validar.
 * @returns {string} Retorna un mensaje de error si la contraseña no cumple con las condiciones.
 */
const password = (value = '') => {
  if (value.length === 0) {
    return 'La contraseña no puede estar vacía.';
  }

  if (value.length < 6) {
    return 'La contraseña debe contener al menos 6 caracteres.';
  }

  if (value.includes(' ')) {
    return 'La contraseña no puede contener espacios en blanco.';
  }

  return '';
};

/**
 * Verifica si una fecha es hoy o futura. Fechas pasadas son rechazadas.
 *
 * @param {string} date Fecha en formato ISO (YYYY-MM-DD) a validar.
 * @returns {boolean} Verdadero si la fecha es hoy o posterior, falso si es pasada.
 */
const dateIsFuture = date => {
  if (!date) return true;
  const today = DateTime.now().startOf('day');
  const compareDate = DateTime.fromISO(date).startOf('day');
  return compareDate >= today;
};

const FormValidations = {
  dayHasPassed,
  dateHasPassed,
  datetimeHasPassed,
  dateIsFuture,
  courseDescription,
  password,
};

export default FormValidations;
